/**
 * Hero behaviour: the entrance reveal, and smooth scrolling for in-page links
 * leaving the hero.
 *
 * REVEAL
 * One orchestrated entrance rather than several independent effects: the photo
 * settles from a slight overscale while the content blocks rise in sequence.
 *
 * Uses IntersectionObserver rather than firing on load, so the section behaves
 * correctly if it is ever moved further down a page. On the homepage the hero
 * is already in view, so it fires on the first frame.
 *
 * The photo is deliberately NOT faded. It is the LCP element, and Chrome does
 * not record a Largest Contentful Paint until the element is visible — an
 * opacity animation would push LCP out by the length of the animation. A
 * transform does not, so the settle is scale-only.
 *
 * ANCHOR SCROLL
 * The social-proof cluster points at the reviews section further down the page.
 * A bare fragment link teleports, which costs the reader any sense of how far
 * they moved or what they passed; scrolling keeps the page's geography intact.
 *
 * Handled here rather than with a global `html { scroll-behavior: smooth }`
 * because that rule is not opt-in — several components in this theme
 * (component-product-form, section-main-product, the sliders) drive their own
 * carousels and swatches through scrollIntoView(), and every one of them would
 * silently inherit the easing.
 */
(() => {
  'use strict';

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* Breathing room below the header, so the target section does not butt
     straight against the top edge of the viewport. */
  const SCROLL_GAP = 16;

  /**
   * Height of the sticky header, if one is currently on screen.
   *
   * Measured per click rather than cached: section-header.js builds the sticky
   * header at runtime and appends it to <body>, so it does not exist yet when
   * this file runs, and it only carries .show while it is actually visible.
   */
  const stickyHeaderHeight = () => {
    const sticky = document.querySelector('.sticky-header.show');
    return sticky ? sticky.getBoundingClientRect().height : 0;
  };

  /**
   * Scroll to the element a fragment names.
   *
   * Returns false when there is nothing to scroll to, so the caller can leave
   * the click alone and let the browser handle it however it normally would.
   */
  const scrollToFragment = (hash) => {
    let id = hash.slice(1);
    // Shopify section ids are plain ASCII, but a hand-entered anchor in the
    // theme editor need not be; a malformed escape must not throw.
    try {
      id = decodeURIComponent(id);
    } catch (e) {
      /* keep the raw value */
    }

    // getElementById rather than querySelector: ids like
    // "shopify-section-template--29946067616091__testimonials_grid_tgktAb"
    // would otherwise need CSS escaping to be a valid selector.
    const target = id ? document.getElementById(id) : null;
    if (!target) return false;

    const top = window.scrollY
      + target.getBoundingClientRect().top
      - stickyHeaderHeight()
      - SCROLL_GAP;

    window.scrollTo({
      top: Math.max(top, 0),
      behavior: reducedMotion.matches ? 'auto' : 'smooth'
    });

    /* Focus has to be moved by hand. A real fragment navigation hands focus to
       the target; preventDefault() cancels that, and without this the next Tab
       press would resume from the top of the document rather than from the
       section the reader was just taken to.

       Sections are not focusable, so borrow a tabindex and give it back on the
       way out — leaving it in place would put a permanent, invisible stop in
       the tab order. preventScroll keeps focus() from fighting the animation
       by jumping to the element outright. */
    if (!target.hasAttribute('tabindex')) {
      target.setAttribute('tabindex', '-1');
      target.addEventListener('blur', () => target.removeAttribute('tabindex'), { once: true });
    }
    target.focus({ preventScroll: true });

    return true;
  };

  class HeroReveal extends HTMLElement {
    connectedCallback() {
      this.bindFragmentLinks();

      // Reveal is a section setting; without it there are no start states in
      // the CSS and nothing for .is-visible to release.
      if (!this.hasAttribute('data-reveal')) return;

      // Anyone who asked for less motion, or whose browser predates
      // IntersectionObserver, goes straight to the finished state.
      if (reducedMotion.matches || !('IntersectionObserver' in window)) {
        this.classList.add('is-visible');
        return;
      }

      this._observer = new IntersectionObserver(
        (entries, observer) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            // One frame's grace so the start state is painted before the
            // transition begins. Without it the browser can collapse both
            // states into a single frame and the animation never shows.
            requestAnimationFrame(() => this.classList.add('is-visible'));
            observer.disconnect();
          }
        },
        { threshold: 0.15 }
      );

      this._observer.observe(this);
    }

    /**
     * Delegated on the hero itself, so it covers every link inside it — the
     * social-proof cluster today, and any CTA a merchant later points at a
     * section anchor in the editor.
     */
    bindFragmentLinks() {
      // connectedCallback re-fires when the theme editor reloads a section, and
      // listeners survive a disconnect. Without this guard an edit session would
      // stack a new handler on every reload.
      if (this._fragmentLinksBound) return;
      this._fragmentLinksBound = true;

      this.addEventListener('click', (event) => {
        // Let anything already handled, and every modified click (new tab, new
        // window, download), go through as the browser intends.
        if (event.defaultPrevented || event.button !== 0) return;
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

        const link = event.target.closest('a[href]');
        if (!link || !this.contains(link)) return;

        /* Same-document fragments only. A link to another page that happens to
           carry a hash has to navigate normally — comparing the resolved origin
           and pathname is what separates the two, since `link.hash` alone is
           true for both. */
        if (!link.hash) return;
        if (link.origin !== window.location.origin) return;
        if (link.pathname !== window.location.pathname) return;
        if (link.target && link.target !== '_self') return;

        if (!scrollToFragment(link.hash)) return;

        event.preventDefault();

        /* Keep the address bar honest: the position stays shareable and Back
           returns to where the reader was, both of which a plain scroll would
           have thrown away. pushState rather than location.hash, which would
           re-trigger the jump this just replaced. */
        if (window.history && typeof history.pushState === 'function') {
          history.pushState(null, '', link.hash);
        }
      });
    }

    disconnectedCallback() {
      if (this._observer) this._observer.disconnect();
    }
  }

  if (!customElements.get('hero-reveal')) {
    customElements.define('hero-reveal', HeroReveal);
  }
})();
